// One settings row for Global settings, a project's Settings tab and a template (architecture sprint-11
// §9.4, §9.5). The control follows the value kind: a Switch for booleans (switch rule, sprint 7), a select
// for choices, the workweek day pills for weekdays. Every change saves immediately; the parent shows errors.
import type { ValueKind } from '@shared/settings-catalogue';
import { ISO_WEEKDAYS, WEEKDAY_SHORT } from '@shared/workweek';
import { Lock } from 'lucide-react';
import { useId } from 'react';
import { PILL_CLASS, PILL_SELECTED_CLASS, PILL_UNSELECTED_CLASS } from '@/components/timer/SwitchDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/cn';
import { browserZone, TimezonePicker } from '@/pages/settings/TimezonePicker';
import type { SettingModeValue } from '@/types/settings';

export type SettingRowContext = 'global' | 'project' | 'template';

export interface SettingRowItem {
  key: string;
  label: string;
  description?: string;
  kind: ValueKind;
  /** The value shown in the control (effective, or the catalogue default for an unset template key). */
  value: unknown;
  /** "Inherited (global)", "Set here", "Not set (inherits)" … */
  badge: string;
  canEdit: boolean;
  /** Shown instead of a working control when the viewer may not change it. */
  lockedLabel?: string;
  /** Mode control (keys a person can also set): shown only when canChooseMode. */
  mode?: SettingModeValue | null;
  canChooseMode?: boolean;
  canReset: boolean;
  /** Extra hint under the row (e.g. "Drives Missing entries."). */
  hint?: string;
}

export interface SettingRowProps {
  item: SettingRowItem;
  context: SettingRowContext;
  onChange: (value: unknown) => void;
  onModeChange?: (mode: SettingModeValue) => void;
  onReset?: () => void;
  pending?: boolean;
}

export const ENFORCED_HINT = "Enforced ignores each person's own preference.";
export const TIMEZONE_DEFAULT_LABEL = 'Not set: uses the default time zone.';
const MODE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'enforced', label: 'Enforced' },
] as const;

function Control({ item, labelId, onChange, disabled }: { item: SettingRowItem; labelId: string; onChange: (v: unknown) => void; disabled: boolean }): JSX.Element {
  const { kind } = item;
  if (kind.type === 'boolean') {
    return <Switch checked={item.value === true} onCheckedChange={(v) => onChange(v)} aria-labelledby={labelId} disabled={disabled} />;
  }
  if (kind.type === 'choice') {
    const index = kind.options.findIndex((o) => o.value === item.value);
    return (
      <NativeSelect
        aria-labelledby={labelId}
        className="w-48"
        value={String(index)}
        disabled={disabled}
        onChange={(e) => {
          const option = kind.options[Number(e.target.value)];
          if (option) onChange(option.value);
        }}
      >
        {kind.options.map((o, i) => (
          <option key={String(o.value)} value={String(i)}>
            {o.label}
          </option>
        ))}
      </NativeSelect>
    );
  }
  if (kind.type === 'localTime') {
    // Sprint 14 (alarm window): 'HH:MM' or empty (null). Saved on change; Clear empties it.
    const time = typeof item.value === 'string' ? item.value : '';
    return (
      <div className="flex items-center gap-2">
        <Input
          type="time"
          className="w-32"
          aria-labelledby={labelId}
          value={time}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.value === '' || /^\d{2}:\d{2}$/.test(e.target.value)) onChange(e.target.value === '' ? null : e.target.value);
          }}
        />
        {time !== '' && (
          <Button variant="ghost" size="sm" disabled={disabled} onClick={() => onChange(null)}>
            Clear
          </Button>
        )}
      </div>
    );
  }
  if (kind.type === 'timezone') {
    // Sprint 13 (roster.zone, "Shift time zone"): null = the server's default zone; the picker starts from the browser zone.
    const zone = typeof item.value === 'string' ? item.value : null;
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="w-64">
          <TimezonePicker value={zone ?? browserZone() ?? 'UTC'} onChange={(tz) => onChange(tz)} disabled={disabled} aria-labelledby={labelId} />
        </div>
        {zone === null && <span className="text-xs text-muted-foreground">{TIMEZONE_DEFAULT_LABEL}</span>}
      </div>
    );
  }
  const days = Array.isArray(item.value) ? (item.value as number[]) : [];
  return (
    <div className="flex flex-wrap gap-1.5" role="group" aria-labelledby={labelId}>
      {ISO_WEEKDAYS.map((day) => {
        const pressed = days.includes(day);
        return (
          <button
            key={day}
            type="button"
            aria-pressed={pressed}
            disabled={disabled || (pressed && days.length === 1)}
            className={cn(PILL_CLASS, 'w-12', pressed ? PILL_SELECTED_CLASS : PILL_UNSELECTED_CLASS)}
            onClick={() => onChange(pressed ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b))}
          >
            {WEEKDAY_SHORT[day - 1]}
          </button>
        );
      })}
    </div>
  );
}

export function SettingRow({ item, context, onChange, onModeChange, onReset, pending = false }: SettingRowProps): JSX.Element {
  const labelId = useId();
  const disabled = !item.canEdit || pending;
  return (
    <div className="flex flex-col gap-3 border-b border-border py-4 last:border-b-0 sm:flex-row sm:items-start sm:justify-between" data-setting={item.key}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span id={labelId} className="text-sm text-foreground">
            {item.label}
          </span>
          <Badge variant="outline">{item.badge}</Badge>
          {item.lockedLabel && (
            <Badge variant="neutral">
              <Lock className="h-3 w-3" aria-hidden="true" />
              {item.lockedLabel}
            </Badge>
          )}
        </div>
        {item.description && <p className="text-xs text-muted-foreground">{item.description}</p>}
        {item.hint && <p className="text-xs text-muted-foreground">{item.hint}</p>}
        {item.canChooseMode && onModeChange && (
          <div className="space-y-1 pt-1">
            <SegmentedControl
              aria-label={`${item.label}: mode`}
              value={item.mode ?? 'default'}
              onChange={(m) => onModeChange(m)}
              options={MODE_OPTIONS}
              disabled={pending || (context === 'project' && item.mode === null && !item.canEdit)}
            />
            <p className="text-xs text-muted-foreground">{ENFORCED_HINT}</p>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Control item={item} labelId={labelId} onChange={onChange} disabled={disabled} />
        {item.canReset && onReset && (
          <Button variant="ghost" size="sm" onClick={onReset} disabled={pending}>
            Reset
          </Button>
        )}
      </div>
    </div>
  );
}
